import { FIREBASE_PATHS } from '../config/firebase';
import { decodeReplayV2 } from '../replay/decoder';
import { completeReplay } from '../replay/recorder';
import type { FreeSpinReplayV2 } from '../replay/schema';
import type { RealtimeDatabasePort } from '../services/firebase';
import { parseJson, type StoragePort } from '../services/storage';
import { PersistentUploadQueue } from '../services/upload-queue';
import {
  buildRemoteExtremeFeature,
  buildRemoteExtremeRun,
  decodeExtremeFeatureEntry,
  decodeExtremeRunEntry,
} from './firebase-codec';
import type {
  CompletedExtremeRun,
  ExtremeFeatureLeaderboardEntry,
  ExtremeRunLeaderboardEntry,
} from './types';

export const EXTREME_LOCAL_STORAGE_KEY = 'slot-yugioh-rework:season3:v1:extreme-results';
const EXTREME_UPLOAD_QUEUE_KEY = 'slot-yugioh-rework:season3:v1:extreme-upload-queue';
const RETAINED_RUNS = 30;
const RETAINED_FEATURES = 10;
const REMOTE_QUERY_LIMIT = 30;
const REMOTE_FEATURE_LIMIT = 10;

type UnknownRecord = Record<string, unknown>;

interface ExtremeEnvelopeV2 {
  readonly version: 2;
  readonly runs: readonly ExtremeRunLeaderboardEntry[];
  readonly features: readonly ExtremeFeatureLeaderboardEntry[];
  readonly featureReplays: readonly FreeSpinReplayV2[];
}

interface ExtremeUploadBundle {
  readonly run: ExtremeRunLeaderboardEntry;
  readonly feature?: ExtremeFeatureLeaderboardEntry;
  readonly replay?: FreeSpinReplayV2;
}

function record(value: unknown): UnknownRecord | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? value as UnknownRecord : null;
}

function decodeUploadBundle(value: unknown): ExtremeUploadBundle | null {
  const source = record(value);
  if (source === null) return null;
  const run = decodeExtremeRunEntry(source.run);
  const feature = source.feature === undefined ? undefined : decodeExtremeFeatureEntry(source.feature);
  const replay = source.replay === undefined ? undefined : decodeReplayV2(source.replay) ?? undefined;
  if (run === null || (feature === undefined) !== (replay === undefined)) return null;
  if (feature && replay && (feature.runId !== run.id || feature.id !== replay.id || replay.betCents !== 200
    || replay.totalWinCents !== feature.winCents || replay.name !== feature.name || !replay.linesComplete)) return null;
  return Object.freeze({ run, ...(feature && replay ? { feature, replay } : {}) });
}

function sortRuns(entries: readonly ExtremeRunLeaderboardEntry[]): readonly ExtremeRunLeaderboardEntry[] {
  return Object.freeze([...entries]
    .sort((left, right) => right.scoreX - left.scoreX || left.createdAt - right.createdAt)
    .slice(0, RETAINED_RUNS));
}

function sortFeatures(entries: readonly ExtremeFeatureLeaderboardEntry[]): readonly ExtremeFeatureLeaderboardEntry[] {
  return Object.freeze([...entries]
    .sort((left, right) => right.xValue - left.xValue || left.createdAt - right.createdAt)
    .slice(0, RETAINED_FEATURES));
}

function mergeById<T extends Readonly<{ id: string }>>(remote: readonly T[], local: readonly T[]): readonly T[] {
  const merged = new Map<string, T>();
  for (const entry of remote) merged.set(entry.id, entry);
  for (const entry of local) if (!merged.has(entry.id)) merged.set(entry.id, entry);
  return [...merged.values()];
}

function mergeFeatures(
  remote: readonly ExtremeFeatureLeaderboardEntry[],
  local: readonly ExtremeFeatureLeaderboardEntry[],
): readonly ExtremeFeatureLeaderboardEntry[] {
  const merged = new Map<string, ExtremeFeatureLeaderboardEntry>();
  for (const entry of remote) merged.set(entry.runId, entry);
  for (const entry of local) if (!merged.has(entry.runId)) merged.set(entry.runId, entry);
  return [...merged.values()];
}

function sameRun(left: ExtremeRunLeaderboardEntry, right: ExtremeRunLeaderboardEntry): boolean {
  return left.id === right.id && left.name === right.name && left.finalBalanceCents === right.finalBalanceCents
    && left.featureCount === right.featureCount && left.createdAt === right.createdAt;
}

function sameFeature(left: ExtremeFeatureLeaderboardEntry, right: ExtremeFeatureLeaderboardEntry): boolean {
  return left.id === right.id && left.runId === right.runId && left.name === right.name
    && left.winCents === right.winCents && left.totalSpins === right.totalSpins && left.createdAt === right.createdAt;
}

function sameReplay(left: FreeSpinReplayV2, right: FreeSpinReplayV2): boolean {
  return left.id === right.id && left.name === right.name && left.betCents === right.betCents
    && left.totalWinCents === right.totalWinCents && left.createdAt === right.createdAt
    && JSON.stringify(left.steps) === JSON.stringify(right.steps);
}

function featureOrder(left: ExtremeFeatureLeaderboardEntry, right: ExtremeFeatureLeaderboardEntry): number {
  return right.xValue - left.xValue || left.createdAt - right.createdAt || left.runId.localeCompare(right.runId);
}

export interface ExtremeCompletionResult {
  readonly run: ExtremeRunLeaderboardEntry;
  readonly bestFeature: ExtremeFeatureLeaderboardEntry | null;
  readonly localRunRank: number | null;
  readonly queued: boolean;
}

export interface ExtremeSyncResult {
  readonly confirmed: number;
  readonly pending: number;
}

export class ExtremeLocalService {
  private runs: readonly ExtremeRunLeaderboardEntry[] = [];
  private features: readonly ExtremeFeatureLeaderboardEntry[] = [];
  private readonly featureReplays = new Map<string, FreeSpinReplayV2>();
  private readonly queue: PersistentUploadQueue<ExtremeUploadBundle>;

  constructor(
    private readonly storage: StoragePort,
    private readonly remote?: RealtimeDatabasePort,
  ) {
    this.restore();
    this.queue = new PersistentUploadQueue(storage, EXTREME_UPLOAD_QUEUE_KEY, { decode: decodeUploadBundle });
  }

  localRuns(): readonly ExtremeRunLeaderboardEntry[] { return this.runs.slice(0, 10); }
  localFeatures(): readonly ExtremeFeatureLeaderboardEntry[] { return this.features.slice(0, 10); }
  localFeatureReplays(): readonly FreeSpinReplayV2[] {
    return [...this.featureReplays.values()].sort((left, right) => right.createdAt - left.createdAt);
  }
  pendingUploads(): number { return this.queue.list().length; }

  async refreshRuns(): Promise<readonly ExtremeRunLeaderboardEntry[]> {
    if (!this.remote || (await this.remote.connect()).status !== 'online') return this.localRuns();
    const children = await this.remote.readChildren(FIREBASE_PATHS.seasonXExtremeRuns, {
      orderByChild: 'scoreX', limitToLast: REMOTE_QUERY_LIMIT,
    });
    const remoteEntries = children
      .map((child) => decodeExtremeRunEntry(child.value, child.key))
      .filter((entry): entry is ExtremeRunLeaderboardEntry => entry !== null);
    this.runs = sortRuns(mergeById(remoteEntries, this.runs));
    this.persist();
    return this.localRuns();
  }

  async refreshFeatures(): Promise<readonly ExtremeFeatureLeaderboardEntry[]> {
    if (!this.remote || (await this.remote.connect()).status !== 'online') return this.localFeatures();
    const children = await this.remote.readChildren(FIREBASE_PATHS.seasonXExtremeFreeSpins, {
      orderByChild: 'xValue', limitToLast: REMOTE_QUERY_LIMIT,
    });
    const remoteEntries = children
      .map((child) => decodeExtremeFeatureEntry(child.value, child.key))
      .filter((entry): entry is ExtremeFeatureLeaderboardEntry => entry !== null);
    const pendingRunIds = new Set(this.queue.list().map((item) => item.payload.run.id));
    const pendingLocal = this.features.filter((entry) => pendingRunIds.has(entry.runId));
    this.features = sortFeatures(mergeFeatures(remoteEntries, pendingLocal));
    this.trimReplayMaps();
    this.persist();
    return this.localFeatures();
  }

  async getFeatureReplay(id: string): Promise<FreeSpinReplayV2 | null> {
    const local = this.featureReplays.get(id);
    if (local) return local;
    if (!this.remote) return null;
    try {
      const replay = decodeReplayV2(await this.remote.readValue(`${FIREBASE_PATHS.seasonXExtremeReplays}/${id}`));
      if (replay === null || !replay.linesComplete || replay.betCents !== 200) return null;
      this.featureReplays.set(replay.id, replay);
      this.trimReplayMaps();
      this.persist();
      return replay;
    } catch { return null; }
  }

  completeRun(completed: CompletedExtremeRun): ExtremeCompletionResult {
    const features = completed.featureDrafts.map((item) => Object.freeze({
      featureIndex: item.featureIndex,
      replay: completeReplay(item.replayDraft, {
        clientEntryId: item.replayDraft.id,
        createdAt: completed.completedAt + item.featureIndex,
      }),
    }));
    const run: ExtremeRunLeaderboardEntry = Object.freeze({
      id: completed.id,
      name: completed.username,
      scoreX: completed.finalBalanceCents / completed.betCents,
      finalBalanceCents: completed.finalBalanceCents,
      featureCount: completed.featureCount,
      createdAt: completed.completedAt,
      replayAvailable: false,
    });
    const best = [...features].sort((left, right) => right.replay.totalWinCents - left.replay.totalWinCents)[0];
    const bestFeature: ExtremeFeatureLeaderboardEntry | null = best === undefined ? null : Object.freeze({
      id: best.replay.id,
      runId: completed.id,
      name: completed.username,
      xValue: best.replay.totalWinCents / best.replay.betCents,
      winCents: best.replay.totalWinCents,
      totalSpins: best.replay.totalSpins,
      createdAt: completed.completedAt,
      replayAvailable: true,
    });

    this.runs = sortRuns([...this.runs.filter((entry) => entry.id !== run.id), run]);
    if (bestFeature !== null && best !== undefined) {
      this.features = sortFeatures([...this.features.filter((entry) => entry.runId !== run.id), bestFeature]);
      this.featureReplays.set(bestFeature.id, best.replay);
    }
    this.trimReplayMaps();
    this.persist();
    this.queue.enqueue(run.id, Object.freeze({
      run,
      ...(bestFeature !== null && best !== undefined ? { feature: bestFeature, replay: best.replay } : {}),
    }));
    const localRunRank = this.runs.findIndex((entry) => entry.id === run.id);
    return Object.freeze({
      run, bestFeature, localRunRank: localRunRank < 0 ? null : localRunRank + 1, queued: true,
    });
  }

  async flushPending(): Promise<ExtremeSyncResult> {
    if (!this.remote || (await this.remote.connect()).status !== 'online') {
      return Object.freeze({ confirmed: 0, pending: this.queue.list().length });
    }
    let confirmed = 0;
    for (const item of this.queue.list()) {
      if (await this.uploadOne(item.payload)) confirmed += 1;
    }
    return Object.freeze({ confirmed, pending: this.queue.list().length });
  }

  private async uploadOne(bundle: ExtremeUploadBundle): Promise<boolean> {
    if (!this.remote) return false;
    const availability = await this.remote.connect();
    if (availability.status !== 'online' || !availability.uid) return false;
    this.queue.attempted(bundle.run.id);
    try {
      const uploadFeature = bundle.feature && bundle.replay
        ? await this.qualifiesForRemoteFeatureTopTen(bundle.feature)
        : false;
      if (uploadFeature && bundle.feature && bundle.replay) {
        const replayPayload = Object.freeze({
          ...bundle.replay,
          seasonId: 'season3', mode: 'extreme', ownerUid: availability.uid, runId: bundle.run.id,
        });
        const replayResult = await this.remote.createOnly(
          `${FIREBASE_PATHS.seasonXExtremeReplays}/${bundle.replay.id}`,
          replayPayload,
        );
        if (replayResult.status === 'exists') {
          const existing = decodeReplayV2(replayResult.existingValue);
          if (existing === null || existing.ownerUid !== availability.uid || !sameReplay(existing, bundle.replay)) return false;
        }
        const featureResult = await this.remote.createOnly(
          `${FIREBASE_PATHS.seasonXExtremeFreeSpins}/${bundle.run.id}`,
          buildRemoteExtremeFeature(bundle.feature, availability.uid),
        );
        if (featureResult.status === 'exists') {
          const existing = decodeExtremeFeatureEntry(featureResult.existingValue, bundle.run.id);
          if (existing === null || existing.ownerUid !== availability.uid || !sameFeature(existing, bundle.feature)) return false;
        }
      }
      const runResult = await this.remote.createOnly(
        `${FIREBASE_PATHS.seasonXExtremeRuns}/${bundle.run.id}`,
        buildRemoteExtremeRun(bundle.run, availability.uid),
      );
      if (runResult.status === 'exists') {
        const existing = decodeExtremeRunEntry(runResult.existingValue, bundle.run.id);
        if (existing === null || existing.ownerUid !== availability.uid || !sameRun(existing, bundle.run)) return false;
      }
      this.queue.acknowledge(bundle.run.id);
      return true;
    } catch { return false; }
  }

  private async qualifiesForRemoteFeatureTopTen(entry: ExtremeFeatureLeaderboardEntry): Promise<boolean> {
    if (!this.remote) return false;
    const children = await this.remote.readChildren(FIREBASE_PATHS.seasonXExtremeFreeSpins, {
      orderByChild: 'xValue', limitToLast: REMOTE_FEATURE_LIMIT,
    });
    if (children.some((child) => child.key === entry.runId)) return true;
    const confirmed = children
      .map((child) => decodeExtremeFeatureEntry(child.value, child.key))
      .filter((candidate): candidate is ExtremeFeatureLeaderboardEntry => candidate !== null)
      .sort(featureOrder);
    if (confirmed.length < REMOTE_FEATURE_LIMIT) return true;
    return featureOrder(entry, confirmed.at(-1)!) < 0;
  }

  private restore(): void {
    const source = record(parseJson(this.storage.getItem(EXTREME_LOCAL_STORAGE_KEY)));
    if ((source?.version !== 1 && source?.version !== 2)
      || !Array.isArray(source.runs) || !Array.isArray(source.features)
      || !Array.isArray(source.featureReplays)) {
      this.storage.removeItem(EXTREME_LOCAL_STORAGE_KEY);
      return;
    }
    this.runs = sortRuns(source.runs
      .map((entry) => decodeExtremeRunEntry(entry))
      .filter((entry): entry is ExtremeRunLeaderboardEntry => entry !== null));
    this.features = sortFeatures(source.features
      .map((entry) => decodeExtremeFeatureEntry(entry))
      .filter((entry): entry is ExtremeFeatureLeaderboardEntry => entry !== null));
    for (const raw of source.featureReplays) {
      const replay = decodeReplayV2(raw);
      if (replay !== null) this.featureReplays.set(replay.id, replay);
    }
    this.trimReplayMaps();
    this.persist();
  }

  private trimReplayMaps(): void {
    const featureIds = new Set(this.features.map((entry) => entry.id));
    for (const id of this.featureReplays.keys()) if (!featureIds.has(id)) this.featureReplays.delete(id);
  }

  private persist(): void {
    const envelope: ExtremeEnvelopeV2 = {
      version: 2,
      runs: this.runs,
      features: this.features,
      featureReplays: [...this.featureReplays.values()],
    };
    this.storage.setItem(EXTREME_LOCAL_STORAGE_KEY, JSON.stringify(envelope));
  }
}
